import { IdcardOutlined, MailOutlined } from '@ant-design/icons';

import type { ElasticPaginatedSelectOption } from '@src/components/ElasticPaginatedSelect/ElasticPaginatedSelect.interfaces';
import type { ElasticSearchResponse } from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { formatNationalId } from '@src/packages/utils/nationalId';
import { formatUserFullName } from '@src/util/format/userFullName';

import type { ElasticUserSource } from './ElasticUserSelect.interfaces';
import styles from './styles.module.less';

/** Primary text of an option; also what tags and filter chips display. */
export const getElasticUserName = (source: ElasticUserSource): string =>
  formatUserFullName(source) || source.displayName?.trim() || '';

const ElasticUserOptionLabel = ({
  name,
  email,
  idCard,
}: {
  name: string;
  email: string;
  idCard: string;
}) => (
  <span className={styles.option}>
    <span className={styles.optionName}>{name}</span>
    {email || idCard ? (
      <span className={styles.optionMetaRow}>
        {email ? (
          <span className={styles.optionMetaEmail}>
            <MailOutlined className={styles.optionMetaIcon} aria-hidden />
            <span className={styles.optionMetaText}>{email}</span>
          </span>
        ) : null}
        {email && idCard ? (
          <span className={styles.optionMetaDivider} aria-hidden>
            ·
          </span>
        ) : null}
        {idCard ? (
          <span className={styles.optionMetaIdCard}>
            <IdcardOutlined className={styles.optionMetaIcon} aria-hidden />
            <span className={styles.optionMetaIdCardText}>{idCard}</span>
          </span>
        ) : null}
      </span>
    ) : null}
  </span>
);

const toSecondaryValue = (value: string, name: string): string =>
  value && value !== name ? value : '';

export const buildElasticUserSelectOptions = (
  response: ElasticSearchResponse<ElasticUserSource>,
): ElasticPaginatedSelectOption[] =>
  (response?.hits?.hits ?? []).map(hit => {
    const source = hit._source ?? {};
    const email = source.email?.trim() ?? '';
    const rawIdCard = source.idCard?.trim() ?? '';
    const idCard = formatNationalId(source.address?.country, rawIdCard) || rawIdCard;
    const name = getElasticUserName(source) || email || idCard || hit._id;
    const secondaryEmail = toSecondaryValue(email, name);
    const secondaryIdCard = toSecondaryValue(idCard, name);
    const title = [name, secondaryEmail, secondaryIdCard].filter(Boolean).join(' · ');

    return {
      value: hit._id,
      selectionLabel: name,
      title,
      label: (
        <ElasticUserOptionLabel
          name={name}
          email={secondaryEmail}
          idCard={secondaryIdCard}
        />
      ),
    };
  });

const toOptionList = (
  options: ElasticPaginatedSelectOption | ElasticPaginatedSelectOption[] | undefined,
): ElasticPaginatedSelectOption[] => {
  if (!options) return [];
  return Array.isArray(options) ? options : [options];
};

/**
 * Plain names for the picked ids, in selection order, so filter panels show
 * only the user name instead of the composed option label.
 */
export const getElasticUserSelectionLabels = (
  userIds: string[],
  options: ElasticPaginatedSelectOption | ElasticPaginatedSelectOption[] | undefined,
): string[] => {
  const optionList = toOptionList(options);

  return userIds.map(
    id => optionList.find(option => option.value === id)?.selectionLabel || id,
  );
};
